import { useEffect, useRef } from 'react'
import { useStore, selectActiveClip } from '../store'
import { clipKept } from '../lib/ranges'
import { getFormat, guideFraction } from '../lib/formats'
import type { LogoConfig, StyleDna } from '../types'

/** CSS filter cho grade màu Style DNA (preview). */
function styleFilter(style: StyleDna): string | undefined {
  if (!style.apply) return undefined
  const g = style.grade
  return `brightness(${g.brightness}) contrast(${g.contrast}) saturate(${g.saturate})`
}

/** Lớp phủ tông ấm-lạnh cho preview. */
function warmthStyle(style: StyleDna): React.CSSProperties | null {
  if (!style.apply || Math.abs(style.grade.warmth) < 0.02) return null
  const w = style.grade.warmth
  const color =
    w > 0
      ? `rgba(255,150,40,${Math.min(0.35, w * 0.3)})`
      : `rgba(40,120,255,${Math.min(0.35, -w * 0.3)})`
  return { position: 'absolute', inset: 0, background: color, pointerEvents: 'none' }
}

/** Style overlay logo cho preview (gần đúng — export mới là bản chuẩn theo pixel). */
function logoStyle(logo: LogoConfig): React.CSSProperties {
  const s: React.CSSProperties = {
    position: 'absolute',
    width: `${logo.scale * 100}%`,
    opacity: logo.opacity,
    pointerEvents: 'none',
  }
  const m = `${logo.margin * 100}%`
  if (logo.pos.includes('t')) s.top = m
  else s.bottom = m
  if (logo.pos.includes('l')) s.left = m
  else s.right = m
  return s
}

/**
 * Player: phát bản dựng ĐÃ CẮT và GHÉP. Chạy hết các khoảng được giữ của clip
 * đang active, rồi tự chuyển sang clip kế tiếp. Logo được phủ lên bằng lớp ảnh.
 */
export function Player() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const rafRef = useRef<number>(0)
  const needFirstSeek = useRef(true)

  const dragging = useRef(false)

  const clips = useStore((s) => s.clips)
  const activeId = useStore((s) => s.activeClipId)
  const playing = useStore((s) => s.playing)
  const pendingSeek = useStore((s) => s.pendingSeek)
  const logo = useStore((s) => s.logo)
  const exportFormats = useStore((s) => s.exportFormats)
  const reframe = useStore((s) => s.reframe)
  const style = useStore((s) => s.style)
  const clip = useStore(selectActiveClip)
  const setPlaying = useStore((s) => s.setPlaying)
  const setActive = useStore((s) => s.setActive)
  const setCurrentTime = useStore((s) => s.setCurrentTime)
  const setFocus = useStore((s) => s.setFocus)

  // Format bị crop đầu tiên → hiển thị khung reframe preview.
  const previewKey = exportFormats.find((k) => k !== 'original')
  const guide =
    clip && previewKey
      ? guideFraction(clip.width, clip.height, getFormat(previewKey), reframe)
      : null
  const focusX = reframe.mode === 'center' ? 0.5 : reframe.fx
  const focusY = reframe.mode === 'center' ? 0.5 : reframe.fy

  const applyFocusFromEvent = (e: React.PointerEvent) => {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const fx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const fy = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
    setFocus(fx, fy)
  }

  const ranges = clip ? clipKept(clip) : []

  // refs mới nhất cho vòng lặp rAF
  const rangesRef = useRef(ranges)
  rangesRef.current = ranges
  const clipsRef = useRef(clips)
  clipsRef.current = clips
  const activeRef = useRef(activeId)
  activeRef.current = activeId

  // đổi clip → cần seek về đầu khoảng giữ đầu tiên khi bắt đầu phát
  useEffect(() => {
    needFirstSeek.current = true
  }, [activeId])

  // seek thủ công (từ timeline / clip list) — hoạt động cả khi đang tạm dừng
  useEffect(() => {
    if (pendingSeek == null) return
    const video = videoRef.current
    if (!video) return
    const apply = () => {
      video.currentTime = pendingSeek
      needFirstSeek.current = false
      useStore.getState().clearPendingSeek()
    }
    if (video.readyState >= 1) apply()
    else video.addEventListener('loadedmetadata', apply, { once: true })
  }, [pendingSeek, activeId])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const advanceClip = () => {
      const cs = clipsRef.current
      const idx = cs.findIndex((c) => c.id === activeRef.current)
      const next = cs[idx + 1]
      if (next) setActive(next.id) // effect chạy lại với clip mới
      else setPlaying(false)
    }

    if (!playing) {
      video.pause()
      cancelAnimationFrame(rafRef.current)
      return
    }

    const tick = () => {
      if (video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }
      const rs = rangesRef.current
      if (rs.length === 0) {
        advanceClip()
        return
      }
      if (needFirstSeek.current) {
        needFirstSeek.current = false
        video.currentTime = rs[0].start
        video.play().catch(() => setPlaying(false))
        rafRef.current = requestAnimationFrame(tick)
        return
      }
      const cur = video.currentTime
      setCurrentTime(cur)
      const active = rs.find((r) => cur >= r.start - 0.05 && cur < r.end)
      if (!active) {
        const nxt = rs.find((r) => r.start >= cur - 0.02)
        if (nxt) video.currentTime = nxt.start
        else return advanceClip()
      } else if (cur >= active.end - 0.04) {
        const i = rs.indexOf(active)
        const nxt = rs[i + 1]
        if (nxt) video.currentTime = nxt.start
        else return advanceClip()
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    video.play().catch(() => {})
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, activeId])

  if (!clip) return null

  return (
    <div className="player">
      <div className="player-frame">
        <video
          ref={videoRef}
          src={clip.url}
          onClick={() => !guide && setPlaying(!playing)}
          style={{ filter: styleFilter(style) }}
          playsInline
        />
        {warmthStyle(style) && <div style={warmthStyle(style)!} />}
        {logo.url && <img src={logo.url} alt="logo" style={logoStyle(logo)} />}

        {guide && (
          <div
            className="focus-layer"
            onPointerDown={(e) => {
              dragging.current = true
              e.currentTarget.setPointerCapture(e.pointerId)
              applyFocusFromEvent(e)
            }}
            onPointerMove={(e) => {
              if (dragging.current) applyFocusFromEvent(e)
            }}
            onPointerUp={(e) => {
              dragging.current = false
              e.currentTarget.releasePointerCapture(e.pointerId)
            }}
          >
            <div
              className="crop-mask"
              style={{
                left: `${guide.left * 100}%`,
                top: `${guide.top * 100}%`,
                width: `${guide.w * 100}%`,
                height: `${guide.h * 100}%`,
              }}
            />
            <div
              className="focus-dot"
              style={{ left: `${focusX * 100}%`, top: `${focusY * 100}%` }}
            >
              🎯
            </div>
            <div className="crop-badge">{getFormat(previewKey!).short}</div>
          </div>
        )}
      </div>
    </div>
  )
}
